import { useCallback, useEffect, useRef, useState } from 'react';
import { ThreeInstance } from '@treedis/tour-loader';
import classNames from 'classnames';
import DeviceManager from '@/components/util/deviceManager';
import { GameLockPath, GameLockPathTransitionType, LockLocationType } from '@/_models/Game';
import { TourImage, TourObject, TourVideo } from '@/_models/virtualStaging/VSObject';
import { ChapterType } from '@/_models/game/GameBook';
import { useLockPath } from './fetch';
import { useGlobalDOMEvents } from '@/hooks/index';
import { color } from '@/utils/index';

export const RegularPaths = ({ showcase, gameId, allTags, polygons, vsObjects, sweeps, clickedElement }: any) => {
  const currentPathStep = useRef<any>(null);
  const paths = useRef(new Map());
  const [isFinish, setIsFinish] = useState<boolean>(false);
  const isMobile = DeviceManager.isMobile();
  const { lockPath } = useLockPath(gameId);

  useGlobalDOMEvents(
    {
      keyup: async (ev: KeyboardEvent) => {
        if (ev.code === 'Space') {
          // eslint-disable-next-line no-use-before-define
          await handleNextStep();
        }
      },
    },
    [vsObjects, allTags],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const goToNextStep = async () => {
    const nextStepId: string = currentPathStep?.current?.next?.id;
    if (nextStepId && paths.current.has(nextStepId)) {
      currentPathStep.current = paths?.current?.get(nextStepId);
    } else {
      setIsFinish(true);
    }
  };

  useEffect(() => {
    if (clickedElement && currentPathStep?.current?.locationItem.toString() === clickedElement.id.toString() && currentPathStep?.current?.locationType === clickedElement.type) {
      goToNextStep();
    }
  }, [clickedElement, goToNextStep]);

  const createPath = (items: Map<string | number, unknown>, triggerType: LockLocationType, triggerItem: number) => {
    const step = lockPath?.paths?.find((path) => path.triggerType === triggerType && path.triggerItem === triggerItem) as GameLockPath & { element: any };

    if (!step) return items;

    const next = lockPath.paths.find((path) => path.triggerType === step.locationType && path.triggerItem === step.locationItem);
    const prev = lockPath.paths.find((path) => path.locationType === step.triggerType && path.locationItem === step.triggerItem);
    if (!triggerType) {
      currentPathStep.current = {
        ...step,
        prev: prev || null,
        next: next && next.id !== step.id ? next : null,
      };
    }
    items.set(step.id, {
      ...step,
      prev: prev || null,
      next: next && next.id !== step.id ? next : null,
    });

    return next && !items.get(next.id) ? createPath(items, next.triggerType, next.triggerItem) : items;
  };

  const rotateCameraTo = async (t) => {
    try {
      const THREE = ThreeInstance.get();

      const l = (value) => (value * Math.PI) / 180;
      const d = new THREE.Vector3(0, 0, -1);
      const a = await showcase.mpSDK.Camera.getPose();
      const r = new THREE.Euler(l(a.rotation.x), l(a.rotation.y), 0, 'XYZ');
      const o = d.clone().applyEuler(r);
      const s = new THREE.Vector3(a.position.x, a.position.y, a.position.z);
      const u = t.clone();
      const f = o.clone().normalize();
      const p = u.clone().sub(s).normalize();
      const m = new THREE.Vector3(f.x, 0, f.z);
      const b = new THREE.Vector3(p.x, 0, p.z);
      const h = m.angleTo(b);
      const g = f.x * p.z - f.z * p.x < 0 ? -1 : 1;
      await showcase.mpSDK.Camera.rotate(((180 * h) / Math.PI) * g);
    } catch (err) {
      console.error(err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const moveToPathStepElement = async (type: ChapterType, value: number | string) => {
    const pathSweep = sweeps.find((sweep) => currentPathStep?.current?.sweepUuid === sweep.uuid);
    let obj: any;
    switch (type) {
      case ChapterType.Tag: {
        const allTagsArray = Array.from(allTags, ([, v]: any) => v);
        obj = allTagsArray.find((item) => value === item?.id || value === item?.defaultSid || value === item?.sid);
        break;
      }
      case ChapterType.Polygon: {
        obj = polygons.find((item) => value === item.id);
        break;
      }
      case ChapterType.VSImage: {
        obj = vsObjects.find((item) => item instanceof TourImage && value === item.vsImageId);
        break;
      }
      case ChapterType.VSVideo: {
        obj = vsObjects.find((item) => item instanceof TourVideo && value === item.vsVideoId);
        break;
      }
      case ChapterType.VSItem: {
        obj = vsObjects.find((item) => item instanceof TourObject && value === item.vsObjectId);
        break;
      }
      default:
        break;
    }
    if (obj && pathSweep) {
      const THREE = ThreeInstance.get();

      const transitionType = currentPathStep?.current?.transitionType || GameLockPathTransitionType.INSTANT;
      await showcase.mpSDK.Sweep.moveTo(pathSweep.uuid, {
        transition: showcase.mpSDK.Sweep.Transition[transitionType?.toUpperCase()],
      });
      await rotateCameraTo(new THREE.Vector3(obj.positionX, obj.positionY, obj.positionZ));
    }
    return obj;
  };

  const handleNextStep = useCallback(async () => {
    await moveToPathStepElement(currentPathStep?.current?.locationType, currentPathStep?.current?.locationItem);
  }, [moveToPathStepElement]);

  useEffect(() => {
    if (lockPath && !paths.current?.size) {
      paths.current = createPath(new Map(), null, -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockPath]);

  return (
    <>
      {currentPathStep?.current && !isFinish && (
        <div
          className={classNames('path-lock-panel', isMobile && 'mobile')}
          style={{ background: color.hexToRgbA(lockPath?.pathSetting.colorBackground, lockPath?.pathSetting.opacityBackground) }}
        >
          <div
            className="path-lock-description"
            dangerouslySetInnerHTML={{ __html: currentPathStep?.current?.description }}
            style={{ color: lockPath?.pathSetting.colorText, opacity: lockPath?.pathSetting.opacityText }}
          />
          <a className="next-btn" onClick={handleNextStep} style={{ background: lockPath?.pathSetting.colorButton || 'none', opacity: lockPath?.pathSetting.opacityButton }}>
            {isMobile ? (
              <span
                className="btn-text"
                style={{
                  color: lockPath?.pathSetting.colorButtonText,
                  opacity: lockPath?.pathSetting.opacityButtonText,
                }}
              >
                Go
              </span>
            ) : (
              <span
                className="btn-text"
                style={{
                  color: lockPath?.pathSetting.colorButtonText,
                  opacity: lockPath?.pathSetting.opacityButtonText,
                }}
              >
                Go Now <i className="fas fa-running" />
              </span>
            )}
          </a>
        </div>
      )}
    </>
  );
};
